/**
 * =============================================================================
 * Project: lajt-online.pl
 * Created Date: 2017-08-03, 11:19:25
 * Author: Michał Rejman <michal.rejman@telestrada.pl>
 * =============================================================================
 * Last Modified: 2018-03-01, 12:54:14
 * Modified By: Sebastian Drzewicki
 * =============================================================================
 * Copyright (c) 2017 Telestrada
 * =============================================================================
 */
import Vue from 'vue';
import store from '@/store';
import sAuth from '@/services/sAuth';
import { mapGetters, mapActions } from 'vuex';
import sDate from '@/services/sDate';

// COMPONENTS //////////////////////////////////////////////////////////////////
import AccountSpeedManagement from '@/components/Panel/AccountSpeedManagement';
import PanelContent from '@/components/Panel/PanelContent';
import PanelFooter from '@/components/Panel/PanelFooter';
import PanelHeader from '@/components/Panel/PanelHeader';
import TopMenu from '@/components/Panel/TopMenu';
import RodoTerms from '@/components/Common/RodoTerms';
import CentralModalFrame from '@/components/Help/CentralModalFrame';
import ServiceChangeConfirmation from '@/components/Modals/ServiceChangeConfirmation';
import ExtendedInformations from '@/components/Modals/ExtendedInformations';

// CONTAINER ///////////////////////////////////////////////////////////////////
const Panel = {
  components: {
    AccountSpeedManagement,
    PanelContent,
    PanelFooter,
    PanelHeader,
    TopMenu,
    RodoTerms,
    CentralModalFrame,
    ServiceChangeConfirmation,
    ExtendedInformations,
  },
  data() {
    return {
      modalInUse: '',
      service: null,
      windowWidth: 0,
    };
  },
  store,
  computed: {
    ...mapGetters(['subscriber', 'brandLogos', 'panelFooterIcons', 'shouldShowRodoTerms', 'acl',
      'pendingStoreActions', 'hasServiceStatusBeenChange', 'confirmationRequired', 'enabled', 'incorrect_code',
    ]),
        // eslint-disable-next-line consistent-return
    prepareProperProps() {
      if (this.modalInUse === 'ExtendedInformations') {
        return {
          service: this.service,
        };
      }
      if (this.modalInUse === 'ServiceChangeConfirmation') {
        return {
          service: this.service,
          number: this.subscriber.number,
          hasStatusBeenChange: this.hasServiceStatusBeenChange,
          confirmationRequired: this.confirmationRequired,
          enabled: this.enabled,
          incorrect_code: this.incorrect_code,
        };
      }
    },
        // eslint-disable-next-line consistent-return
    currentComponent() {
      if (this.modalInUse === 'ExtendedInformations') {
        return ExtendedInformations;
      }
      if (this.modalInUse === 'ServiceChangeConfirmation') {
        return ServiceChangeConfirmation;
      }
    },
  },
  methods: {
    ...mapActions(['setSubscriberMainData', 'setSubscriberSettings', 'setSubsriberAclForGroupsOfNumbers',
      'setRechargesTerms', 'setBillingTerms', 'getRechargesList', 'getBillingList',
      'getBannerList', 'showMobileMenu', 'hideMobileMenu', 'getInformationAboutMainComponentsVisibility',
      'getPanelFooterIcons', 'getFaqList', 'getCustomerPaymentsData', 'getCustomerInvoicesData',
      'getRodoTerms', 'changeStatus', 'getServicesList', 'clearChangeStatusResult', 'confirmStatusChange',
    ]),
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    __callDedicatedForPostpaidVuexActions() {
      if (this.acl.privileges.can_see_payments) {
        this.getCustomerPaymentsData();
        this.getCustomerInvoicesData();
      }
    },
    __callDedicatedForPrepaidVuexActions() {
      const yesterday = sDate.formatDate((d => new Date(d.setDate(d.getDate() - 1)))(new Date()));
      const currentMonthStart = sDate.getDateByDay('firstDay', 'currentMonth');
      this.setRechargesTerms({ dateStart: currentMonthStart, dateEnd: yesterday })
                .then(this.getRechargesList);
    },
    __callDedicatedForBothAccountTypeVuexActions() {
      const { payload, settings } = sAuth.getDataFromToken();
      const yesterday = sDate.formatDate((d => new Date(d.setDate(d.getDate() - 1)))(new Date()));
      const currentMonthStart = sDate.getDateByDay('firstDay', 'currentMonth');
      this.setSubscriberMainData(payload);
      this.setSubscriberSettings(settings);
      this.setSubsriberAclForGroupsOfNumbers(payload);
      this.setBillingTerms({ dateStart: currentMonthStart, dateEnd: yesterday })
                .then(this.getBillingList);
      this.getBannerList();
      this.getInformationAboutMainComponentsVisibility();
      this.getPanelFooterIcons();
      this.getFaqList();
    },
    async handleChangeStatus(service, status) {
      const params = {
        feature: service,
        is_active: !status,
      };
      await this.changeStatus(params);
    },
    async handleConfirmStatusChange(service, status, smsCode) {
      const params = {
        feature: service,
        is_active: !status,
        sms_code: smsCode,
      };
      await this.changeStatus(params);
    },
    closeModal() {
      this.modalInUse = '';
      this.service = null;
    },
  },
  beforeMount() {
    this.__callDedicatedForBothAccountTypeVuexActions();

    if (this.subscriber.type === 'prepaid') {
      this.__callDedicatedForPrepaidVuexActions();
    }
    if (this.subscriber.type === 'postpaid') {
      this.__callDedicatedForPostpaidVuexActions();
    }
  },
  beforeDestroy() {
    this.$EventBus.$off('Services/CLOSE_MODAL');
    this.$EventBus.$off('ServiceChangeConfirmation/CHANGE_STATUS');
    this.$EventBus.$off('ServiceChangeConfirmation/CLOSE_MODAL');
    this.$EventBus.$off('ExtendedInformations/CLOSE_MODAL');
    this.$EventBus.$off('Service/TOGGLE_MODAL');
    this.$EventBus.$off('Service/TOGGLE_MODAL_WITH_STATUS_CHANGE');
    this.$EventBus.$off('ServiceChangedConfirmation/CANCEL_CONFIRMATION');
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
      this.onResize();
    });
    this.$EventBus.$on('ServiceChangeConfirmation/CHANGE_STATUS', (service, status) => {
      this.handleChangeStatus(service, status).then();
    });
    this.$EventBus.$on('ServiceChangeConfirmation/CLOSE_MODAL', () => {
      this.closeModal();
      this.$store.commit('TOGGLE_SERVICE_STATUS_CHANGE_RESULT', false);
    });
    this.$EventBus.$on('ServiceChangedConfirmation/CANCEL_CONFIRMATION', () => {
      this.closeModal();
    });
    this.$EventBus.$on('ServiceChangedConfirmation/SUBMIT_CONFIRMATION', (service, status, smsCode) => {
      this.handleConfirmStatusChange(service, status, smsCode);
    });
    this.$EventBus.$on('ExtendedInformations/CLOSE_MODAL', () => {
      this.closeModal();
    });
    this.$EventBus.$on('Services/CLOSE_MODAL', () => {
      this.closeModal();
    });
    this.$EventBus.$on('Service/TOGGLE_MODAL', (service) => {
      this.service = service;
      this.modalInUse = 'ExtendedInformations';
    });
    this.$EventBus.$on('Service/TOGGLE_MODAL_WITH_STATUS_CHANGE', (service) => {
      this.service = service;
      this.modalInUse = 'ServiceChangeConfirmation';
    });
  },
  template: `
        <div>
            <CentralModalFrame v-if="modalInUse!==''">
                <template slot="modal">
                    <component :is="currentComponent"
                               v-bind="prepareProperProps"
                    />
                </template>
            </CentralModalFrame>
            <div class="my-container">
                <RodoTerms/>
                <v-touch @swipeleft="hideMobileMenu" @swiperight="showMobileMenu"
                         v-bind:swipe-options="{ direction: 'horizontal' }">
                    <panel-header :brand-logos="brandLogos">
                        <account-speed-management slot="account-speed-management" :subscriber="subscriber"/>
                    </panel-header>
                    <top-menu :subscriber="subscriber" :brand-logos="brandLogos"/>
                    <panel-content/>
                    <panel-footer :subscriber="subscriber"
                                  :footer-icons="panelFooterIcons"
                                  :windowWidth="windowWidth"
                    />
                </v-touch>
            </div>
        </div>
    `,
};

export default Vue
    .component('Panel', Panel);



// WEBPACK FOOTER //
// ./src/containers/Panel/index.js